<script setup>
import { ref, computed, watchEffect } from 'vue'
import { useStore } from 'vuex'
import { MapLayer, MapSource, MapFeature } from '@/components/map'
import {
  LayerPlanetVars,
  LayerPlanetscope,
  LayerOrder,
  LayerRadar,
  LayerCollectedData,
  LayerStatic
} from '@/components/layers'
import { TriangleAlert } from 'lucide-vue-next'

const emit = defineEmits(['isLoaded', 'deleteLayer'])
const props = defineProps({
  map: Object,
  item: Object,
  beforeId: String,
  layerId: String,
  orgId: Number,
  isVisible: Boolean,
})

const store = useStore()

// Get the geometry from the layer data
const layerGeometry = computed(() => {
  // Check for geometry in different possible locations
  return props.item?.properties?.layer?.geometry ||
         props.item?.geometry ||
         null
})
</script>

<template>
<div class="ml-2">
  <div>
    <!-- Geometry outline for the layer -->
    <MapSource
      v-if="layerGeometry && isVisible"
      :map="map"
      type="geojson"
      :id="`${layerId}-geometry-source`"
      :data="{
        type: 'Feature',
        geometry: layerGeometry,
        properties: {}
      }"
    >
      <MapLayer
        :map="map"
        :id="`${layerId}-geometry-outline`"
        :source="`${layerId}-geometry-source`"
        type="line"
        :before-id="beforeId"
        :options="{
          paint: {
            'line-color': '#1E90FF',
            'line-width': 2,
            'line-opacity': 0.8
          },
          layout: {
            visibility: 'visible'
          }
        }"
      />
    </MapSource>

    <!-- Main layer source for features -->
    <MapSource
      :map="map"
      type="geojson"
      :id="layerId + '-source'"
      :data="{
        type: 'FeatureCollection',
        features: []
      }"
    >
      <MapLayer
        :map="map"
        :id="layerId"
        :source="layerId + '-source'"
        type="fill"
        :before-id="beforeId"
      >
        <div>

          <LayerPlanetscope
            v-if="item.properties.type === 0"
            :map="map"
            :before-id="beforeId"
            :group-id="layerId"
            :org-id="orgId"
            :layer-id="layerId"
            :options="{
              layout: {
                visibility: isVisible ? 'visible' : 'none',
              },
            }"
            @isLoaded="emit('isLoaded', $event)"
          />

          <!-- <div
            class="p-1"
            v-if="item.properties.type === 0"
          >
            <div class="flex items-center text-amber-700">
              <TriangleAlert class="w-5 h-5 mr-2 flex-shrink-0" />
              <div>
                <h3 class="font-medium">PlanetScope Access Required</h3>
              </div>
            </div>
          </div> -->

          <LayerOrder
            :map="map"
            :layer="item.properties"
            :layer-id="layerId"
            v-if="item.properties.type === 1"
            :before-id="beforeId"
            :group-id="layerId"
            :options="{
              minzoom: 0,
              maxzoom: 22,
              layout: {
                visibility: isVisible ? 'visible' : 'none',
              },
            }"
            @isLoaded="emit('isLoaded', $event)"
          />

          <LayerPlanetVars
            v-if="item.properties.type === 2"
            :map="map"
            :layer-id="layerId"
            :code="item.properties.code"
            :layer="item.properties.layer"
            :before-id="beforeId"
            :group-id="layerId"
            :isVisible="isVisible"
            :options="{
              layout: {
                visibility: isVisible ? 'visible' : 'none',
              },
            }"
            @isLoaded="emit('isLoaded', $event)"
            :org-id="orgId"
          />


          <LayerRadar
            v-if="item.properties.type === 3"
            :map="map"
            :layer-id="layerId"
            :code="item.uuid"
            :layer="item.properties.layer"
            :before-id="beforeId"
            :group-id="layerId"
            :is-visible="isVisible"
            :options="{
              layout: {
                visibility: isVisible ? 'visible' : 'none',
              },
            }"
            @isLoaded="emit('isLoaded', $event)"
            :org-id="orgId"
          />

          <LayerCollectedData
            v-if="item.properties.type === 4"
            :map="map"
            :layer-id="layerId"
            :code="item.uuid"
            :layer="item.properties.layer"
            :org-id="orgId"
            :before-id="beforeId"
            :group-id="layerId"
            :options="{
              layout: {
                visibility: isVisible ? 'visible' : 'none',
              },
            }"
            @isLoaded="emit('isLoaded', $event)"
          />

          <LayerStatic
            v-if="item.properties.type === 5"
            :map="map"
            :layer-id="layerId"
            :code="item.uuid"
            :layer="item.properties.layer"
            :org-id="orgId"
            :before-id="beforeId"
            :group-id="layerId"
            :options="{
              layout: {
                visibility: isVisible ? 'visible' : 'none',
              },
            }"
            @isLoaded="emit('isLoaded', $event)"
          />
        </div>


      </MapLayer>
    </MapSource>
  </div>
</div>
</template>
